import { useMemo } from "react";

import { RewardMint, UserExternalRewardsInfoExpanded, useOraclePrices } from "@bridgesplit/abf-react";
import { Column, OverlappingImages, Row, Tooltip, Text, Image } from "@bridgesplit/ui";
import { formatTokenAmount, formatUsd } from "@bridgesplit/utils";

const MAX_EXTERNAL_REWARD_IMAGES = 5;

export function VaultExternalRewards({
    externalRewards
}: {
    externalRewards: Record<RewardMint, UserExternalRewardsInfoExpanded> | undefined;
}) {
    const rewards = useMemo(() => {
        return externalRewards ? Object.values(externalRewards) : [];
    }, [externalRewards]);

    const images = useMemo(() => {
        return rewards.map((r) => ({ src: r.rewardMetadata.imageUrl, variant: "circle" as const }));
    }, [rewards]);

    if (rewards.length === 0) {
        return null;
    }

    return (
        <Tooltip
            placement="bottom-start"
            minTooltipWidth="200px"
            reverseColors
            title={<VaultExternalRewardsTooltip rewards={rewards} />}
        >
            <OverlappingImages images={images} size="20px" maxLength={MAX_EXTERNAL_REWARD_IMAGES} />
        </Tooltip>
    );
}

export function VaultExternalRewardsTooltip({ rewards }: { rewards: UserExternalRewardsInfoExpanded[] }) {
    const mints = useMemo(() => rewards.map((r) => r.rewardMint), [rewards]);
    const { getOracle } = useOraclePrices(mints);

    return (
        <Column padding={0.5} spacing={0.5}>
            {rewards.map((reward) => {
                const usdPrice = getOracle(reward.rewardMint)?.usdPrice ?? 0;
                const usdValue = (reward.pendingRewards ?? 0) * usdPrice;

                return (
                    <Row key={reward.rewardMint} sx={{ alignItems: "center", gap: 1 }} spaceBetween>
                        <Row spacing={0.5}>
                            <Image src={reward.rewardMetadata.imageUrl} size="16px" variant="circle" />
                            <Text variant="body2">{reward.rewardMetadata.symbol}</Text>
                        </Row>
                        <Row spacing={0.5}>
                            <Text variant="body2">
                                {formatTokenAmount(reward.pendingRewards, {
                                    symbol: reward.rewardMetadata.symbol,
                                    hideSymbol: true
                                })}
                            </Text>
                            <Text variant="body2" color="disabled">
                                {formatUsd(usdValue)}
                            </Text>
                        </Row>
                    </Row>
                );
            })}
        </Column>
    );
}
